import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LockKey, SpinnerGap } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const ADMIN_PERMS = ["reports", "annuaire", "contacts", "documents"];

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim().toLowerCase(), password.trim());
    setLoading(false);
    if (res.ok) {
      const u = res.user;
      const hasAdminArea = u.role === "admin" || (u.permissions || []).some((p) => ADMIN_PERMS.includes(p));
      navigate(hasAdminArea ? "/admin" : "/entretien");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-5" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-sm rounded-3xl border border-black/5 bg-white p-8 shadow-[0_18px_50px_rgb(0,0,0,0.08)]"
      >
        <div className="flex flex-col items-center">
          <Link to="/" data-testid="login-logo-link" aria-label="Retour à l'accueil">
            <Logo size={48} />
          </Link>
          <h1 className="mt-4 font-display text-2xl font-extrabold tracking-tight">Espace gestion</h1>
          <p className="mt-1 text-center text-xs text-muted-foreground">
            Gestionnaire, concierge ou conseil syndical — connectez-vous avec le compte qui vous a été créé.
          </p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="mb-1.5 block text-xs font-bold text-muted-foreground">Email</label>
            <input
              id="admin-email"
              type="email"
              required
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.fr"
              data-testid="admin-email-input"
              className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-1.5 block text-xs font-bold text-muted-foreground">Mot de passe</label>
            <input
              id="admin-password"
              type="password"
              required
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="admin-password-input"
              className="w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
            />
          </div>
          {error && (
            <p className="rounded-2xl bg-[#FFE5E5] px-4 py-3 text-xs font-bold text-[#C90A0A]" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition-colors duration-200 hover:bg-[#265442] disabled:opacity-60"
          >
            {loading ? <SpinnerGap size={18} className="animate-spin" /> : <LockKey size={16} weight="bold" />}
            Se connecter
          </button>
        </form>
        <Link to="/" className="mt-6 block text-center text-xs font-semibold text-muted-foreground transition-colors hover:text-primary" data-testid="login-back-home">
          Retour au portail résidents
        </Link>
      </motion.div>
    </div>
  );
}
